import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, EditIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { Layout } from '../';
import AllOrganizations from './allOrganizations';
import { useAsyncApi } from '../../utils/useAsyncApi';
import { getOrganization } from './api';
import SessionList from './sessionList';
import SessionSearch from './sessionSearch';
import { openModal, closeModal, Modals } from '../../redux/reducers';
import EditUserModal from './editUserModal';
import { InfoModal } from '../';

const SingleOrganization = ({ organizationId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userData } = useSelector((state) => state.user);
  const canManageUsers =
    userData.organization && (userData.organization.isAdmin || userData.organization.isSuperUser);
  const loadOrganization = useCallback(async () => {
    return getOrganization(organizationId);
  }, [organizationId]);
  const { isLoading, response: organization, error, reload } = useAsyncApi(
    organizationId ? loadOrganization : null
  );

  const [sessions, setSessions] = useState(null);
  const setSearchResults = useCallback((sessions, searchOptions) => {
    const { start, end, interpreter } = searchOptions;
    let title = `${moment(start).format('D.M.YYYY')} - ${moment(end).format('D.M.YYYY')}`;
    if (interpreter) {
      title = interpreter.name + ', ' + title;
    }
    setSessions({ list: sessions, title });
  });
  const [isSearchFormOpen, setSearchFormOpen] = useState(false);
  const toggleSearchForm = useCallback(() => setSearchFormOpen((o) => !o), [setSearchFormOpen]);

  const { showModal, modalProps, modalName } = useSelector((state) => state.ui);
  const openCreateModal = useCallback(() => {
    dispatch(openModal({ createNew: true }, Modals.EDIT_USER));
  }, []);

  return (
    <Box>
      {isLoading ? (
        <p>{t('organization.loading')}</p>
      ) : error ? (
        <p>
          {t('organization.error')} {error.message || `${error}`}
        </p>
      ) : (
        <>
          <EditUserModal
            open={showModal === true && modalName === Modals.EDIT_USER}
            onUpdate={reload}
            organizationId={organizationId}
            {...modalProps}
          />
          <InfoModal
            open={showModal && modalName === Modals.ERROR}
            onRightButtonClick={() => dispatch(closeModal())}
            onClose={() => dispatch(closeModal())}
            leftButtonText={null}
            {...modalProps}
          />
          <Flex flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}>
            <Box width={[null, null, null, '30%']} minWidth="480px">
              <Flex>
                <Heading mb="4">{organization.name}</Heading>
                {canManageUsers && (
                  <Button ml="4" onClick={openCreateModal}>
                    <AddIcon />
                  </Button>
                )}
              </Flex>
              <Text>{organization.description}</Text>

              <Table
                mt="4"
                position="relative"
                sx={{ Th: { px: 2 }, Td: { px: 2, verticalAlign: 'top' } }}
              >
                <Thead>
                  <Tr>
                    <Th width="40%">{t('organization.interpreterName')}</Th>
                    <Th width="45%">{t('organization.interpreterEmail')}</Th>
                    <Th>{t('organization.interpreterAdmin')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {organization.members.length === 0 && (
                    <Tr>
                      <Td>{t('organization.noMembers')}</Td>
                    </Tr>
                  )}
                  {(organization.members || []).map((member) => {
                    return (
                      <Tr
                        key={member.id}
                        sx={{
                          ':hover': {
                            'bg': '#eee',
                            '& .editButton': {
                              display: 'inline',
                            },
                          },
                        }}
                      >
                        <Td>
                          {member.name}
                          {canManageUsers && (
                            <Button
                              variant="plain"
                              my="-20px"
                              mr="-30px"
                              display="none"
                              className="editButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(
                                  openModal({ createNew: false, user: member }, Modals.EDIT_USER)
                                );
                              }}
                            >
                              <EditIcon />
                            </Button>
                          )}
                        </Td>
                        <Td>{member.email}</Td>
                        <Td>{member.isAdmin ? t('organization.yes') : t('organization.no')}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            {(sessions || organization.sessions) && (
              <>
                <Box
                  width="1px"
                  backgroundColor="#eee"
                  alignSelf="stretch"
                  mt="24"
                  mx="3"
                  display={['none', 'none', 'none', 'block']}
                />
                <Box flexGrow="1">
                  <Flex alignItems="baseline">
                    <Heading mt="10" mb="4">
                      {sessions ? sessions.title : t('organization.lastSessions')}
                    </Heading>
                    <Button variant="plain" ml="2" onClick={toggleSearchForm}>
                      {t('organization.search.toggleButton')}
                      {isSearchFormOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Button>
                  </Flex>
                  {isSearchFormOpen && (
                    <SessionSearch
                      organization={organization._id}
                      interpreters={organization.members}
                      setSearchResults={setSearchResults}
                    />
                  )}
                  <SessionList
                    sessions={(sessions && sessions.list) || organization.sessions}
                    members={organization.members}
                  />
                </Box>
              </>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
};

SingleOrganization.propTypes = {
  organizationId: PropTypes.string,
};

const OrganizationPage = ({ history, match }) => {
  const { userData } = useSelector((state) => state.user);
  const isSuperUser = userData.organization && userData.organization.isSuperUser;
  const organizationId = match.params.organizationId || userData.organization.id;

  return (
    <Layout contentProps={{ width: '100%', px: '2%' }}>
      {isSuperUser && !organizationId ? (
        <AllOrganizations history={history} />
      ) : (
        <SingleOrganization organizationId={organizationId} />
      )}
    </Layout>
  );
};

OrganizationPage.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
};

export default OrganizationPage;
